import { OAuthPopup } from '@tasoskakour/react-use-oauth2'
import React from 'react'
import { Outlet, createBrowserRouter } from 'react-router-dom'

import { createFeatureDecisions } from 'config/featureDecisions'
import {
	BATTLE_SHIPS,
	BUILDING_BLOCKS,
	CUSTOM_GAMES,
	DRESS_UP,
	EXPIRED_PAGE,
	GAMES_PAGE,
	HOME_PAGE,
	LEAP_FROG,
	LEGAL_PAGE,
	LOGGED_OUT_PAGE,
	MUSICAL_CHAIRS,
	NO_ACCESS_PAGE,
	OPPOSITE_DAY,
	PROFILE_PAGE,
	PUBLISHED_GAMES,
	PUBLISHED_GAME_ROUTE_TEMPLATE,
	ROUND_ROBIN,
	SIMON_SAYS,
	SUBMITTED_IDEAS,
	SUPPORT,
	TEETER_TOTTER,
} from 'constants/routes'

import { TimeoutProvider } from 'contexts/TimeoutProvider'

import BattleShips from 'pages/BattleShips'
import BuildingBlocks from 'pages/BuildingBlocks'
import DressUp from 'pages/DressUp'
import ExpiredPage from 'pages/ExpiredPage'
import HomePage from 'pages/HomePage'
import LeapFrog from 'pages/LeapFrog'
import LegalPage from 'pages/Legal'
import LoggedOutPage from 'pages/LoggedOutPage'
import MusicalChairs from 'pages/MusicalChairs'
import OppositeDay from 'pages/OppositeDay'
import ProfilePage from 'pages/Profile/Profile'
import PublishedGame from 'pages/PublishedGame'
import PublishedGames from 'pages/PublishedGames'
import RoundRobinPage from 'pages/RoundRobin'
import SimonSays from 'pages/SimonSays'
import SubmittedIdeas from 'pages/SubmittedIdeas'
import Support from 'pages/Support'
import TeeterTotter from 'pages/TeeterTotter'

import ArcNavBarComponent from 'components/ArcNavBar'
import ErrorPage from 'components/ErrorPage/ErrorPage'
import Footer from 'components/Footer'
import RequireAuth from 'components/RequireAuth/RequireAuth'

const TWENTY_FIVE_MINUTES_MILLISECONDS = 25 * 60 * 1000
const FIVE_MINUTES_MILLISECONDS = 5 * 60 * 1000

function Layout() {
	return (
		<>
			<ArcNavBarComponent />
			<main>
				<Outlet />
			</main>
			<Footer />
		</>
	)
}

const router = createBrowserRouter([
	{
		element: (
			<TimeoutProvider
				timeoutDuration={TWENTY_FIVE_MINUTES_MILLISECONDS}
				warningDuration={FIVE_MINUTES_MILLISECONDS}
			>
				<Layout />
				{/* Layout includes: NavBar and Footer */}
			</TimeoutProvider>
		),
		children: [
			{ path: '/callback', element: <OAuthPopup /> },
			{
				path: HOME_PAGE,
				element: (
					<RequireAuth guestUserAllowed={false}>
						<HomePage />
					</RequireAuth>
				),
			},
			{
				path: SUPPORT,
				element: <Support />, // Public Page
			},
			{
				path: PROFILE_PAGE,
				element: (
					<RequireAuth guestUserAllowed={false}>
						<ProfilePage />
					</RequireAuth>
				),
			},
			{
				path: GAMES_PAGE,
				children: [
					{
						path: BATTLE_SHIPS,
						element: (
							<RequireAuth guestUserAllowed={false}>
								<BattleShips />
							</RequireAuth>
						),
					},
					{
						path: BUILDING_BLOCKS,
						element: (
							<RequireAuth guestUserAllowed={false}>
								<BuildingBlocks />
							</RequireAuth>
						),
					},
					{
						path: SIMON_SAYS,
						element: (
							<RequireAuth guestUserAllowed={false}>
								<SimonSays />
							</RequireAuth>
						),
					},
					{
						path: LEAP_FROG,
						element: (
							<RequireAuth guestUserAllowed={false}>
								<LeapFrog />
							</RequireAuth>
						),
					},
					{
						path: DRESS_UP,
						element: (
							<RequireAuth guestUserAllowed={false}>
								<DressUp />
							</RequireAuth>
						),
					},
					{
						path: MUSICAL_CHAIRS,
						element: (
							<RequireAuth guestUserAllowed={false}>
								<MusicalChairs />
							</RequireAuth>
						),
					},
					{
						path: OPPOSITE_DAY,
						element: (
							<RequireAuth guestUserAllowed={false}>
								<OppositeDay />
							</RequireAuth>
						),
					},
					{
						path: ROUND_ROBIN,
						element: (
							<RequireAuth guestUserAllowed={false}>
								<RoundRobinPage />
							</RequireAuth>
						),
					},
					{
						path: TEETER_TOTTER,
						element: (
							<RequireAuth guestUserAllowed={false}>
								<TeeterTotter />
							</RequireAuth>
						),
					},
				],
			},
			{
				path: PUBLISHED_GAME_ROUTE_TEMPLATE,
				element: (
					<RequireAuth guestUserAllowed={false}>
						<PublishedGame />
					</RequireAuth>
				),
			},
			{
				path: createFeatureDecisions().enableRedesignedGamePages()
					? CUSTOM_GAMES
					: PUBLISHED_GAMES,
				element: (
					<RequireAuth guestUserAllowed={false}>
						<PublishedGames />
					</RequireAuth>
				),
			},
			{
				path: PROFILE_PAGE,
				element: (
					<RequireAuth guestUserAllowed={false}>
						<ProfilePage />
					</RequireAuth>
				),
			},
			{
				path: SUBMITTED_IDEAS,
				element: (
					<RequireAuth guestUserAllowed={false}>
						<SubmittedIdeas />
					</RequireAuth>
				),
			},
			{
				path: EXPIRED_PAGE,
				element: (
					<RequireAuth>
						<ExpiredPage />
					</RequireAuth>
				),
			},
			{
				path: NO_ACCESS_PAGE,
				element: (
					<RequireAuth guestUserAllowed={false}>
						<ErrorPage message="You no longer have platform access because the custom game you were invited to has expired" />
					</RequireAuth>
				),
			},
			{
				path: '*',
				element: (
					<RequireAuth guestUserAllowed={true}>
						<ErrorPage message="You don't have access to this page, or the page is no longer active" />
					</RequireAuth>
				),
			},
			{ path: LOGGED_OUT_PAGE, element: <LoggedOutPage /> },
			{ path: LEGAL_PAGE, element: <LegalPage /> },
		],
	},
])

export default router
